import type {
  FavoritePromptItem,
  RequestBodyFavoritePrompt,
  RequestQueryFavoritePrompt,
  ResponsePresetPrompt,
} from "@/utils/types/chat";
import type {
  ChatMessage,
  RequestBodySendPromptAuto,
  RequestQuerySummaryReport,
  RequestQuerySummaryReportWithSale,
  ResponseSendPromptAuto,
} from "@/utils/types/conversation";

export type ChatStreamStatus =
  | "open" // new connection
  | "submitted"
  | "streaming"
  | "ready"
  | "error"
  | "abort";

export const useChatStore = defineStore("chat-store", () => {
  const dayjs = useDayjs();
  const authStore = useAuthStore();
  const { user, apiAutoPromptVersion } = storeToRefs(authStore);

  const sessionId = ref();
  const channelId = ref();

  const promptSelect = ref("summary");
  const promptId = ref();
  const promptText = ref({
    name: "",
    prompt: "",
  });
  const promptNeedImage = ref(false);

  const filters = ref<
    RequestQuerySummaryReport | RequestQuerySummaryReportWithSale
  >();
  const messages = ref<ChatMessage[]>([]);
  const status = ref<ChatStreamStatus>("ready");
  const promptFilterOptions = ref<ResponsePresetPrompt[]>([]);

  const isSending = computed(() =>
    ["open", "submitted", "streaming"].includes(status.value),
  );
  const isPrinting = ref(false);

  const sendSessionAbortController = ref();
  const sendSSEAbortController = ref();

  const getFavoritePrompts = async (query?: RequestQueryFavoritePrompt) => {
    let url = `/company/v1/favorite-prompts/`;
    if (query) {
      const params = new URLSearchParams(convertToStringObject(query));
      url += "?" + params;
    }
    const res = await useApi<FavoritePromptItem[]>(url, {
      method: "get",
    });
    return res;
  };

  const createFavoritePrompt = async (body: RequestBodyFavoritePrompt) => {
    const res = await useApi<FavoritePromptItem>(
      `/company/v1/favorite-prompts/`,
      {
        method: "POST",
        body: body,
      },
    );
    return res;
  };

  const updateFavoritePromptById = async (
    id: number | string,
    body: Partial<RequestBodyFavoritePrompt>,
  ) => {
    const res = await useApi<FavoritePromptItem>(
      `/company/v1/favorite-prompts/${id}`,
      {
        method: "patch",
        body: body,
      },
    );
    return res;
  };

  const deleteFavoritePromptById = async (id: number | string) => {
    const res = await useApi(`/company/v1/favorite-prompts/${id}`, {
      method: "DELETE",
    });
    return res;
  };

  const toggleFavoritePromptById = async (id: number | string) => {
    const res = await useApi<FavoritePromptItem>(
      `/company/v1/favorite-prompts/${id}/toggle/active`,
      {
        method: "POST",
      },
    );
    return res;
  };

  const getPresetPrompts = async () => {
    const res = await useApi<ResponsePresetPrompt[]>(
      `/company/v1/preset-prompts/`,
      {
        method: "get",
      },
    );
    return res;
  };

  const resetSSE = () => {
    sessionId.value = undefined;
    channelId.value = undefined;
    messages.value = [];
    status.value = "ready";
  };

  const getErrorMessage = (err: any) => err?.data?.message || err?.message;

  const createAssistantMessage = (content = ""): ChatMessage => ({
    id: `_assistant`,
    role: "assistant",
    seeMore: false,
    seeFileMore: false,
    content,
    created: dayjs().format("YYYY-MM-DD HH:mm:ss"),
  });

  const createUserMessage = (
    body: RequestBodySendPromptAuto<
      RequestQuerySummaryReport | RequestQuerySummaryReportWithSale | undefined
    >,
  ): ChatMessage => ({
    id: `_user`,
    role: "user",
    user_name: [user.value?.first_name, user.value?.last_name].join(" "),
    content: body.context,
    seeMore: false,
    seeFileMore: false,
    created: dayjs().format("YYYY-MM-DD HH:mm:ss"),
  });

  const getLastAssistantMessage = () =>
    messages.value.findLast((message) => message.id === "_assistant");

  const ensureAssistantMessage = (content = "") => {
    const [lastItem] = messages.value.slice(-1);

    if (lastItem !== undefined && lastItem.role === "assistant") {
      if (lastItem.content === undefined) {
        lastItem.content = "";
      }

      return lastItem;
    }

    const assistantMessage = createAssistantMessage(content);
    messages.value.push(assistantMessage);
    return assistantMessage;
  };

  const updateUserConversationFromEvent = (
    event: Extract<StreamEvent, { type: "start" }>,
  ) => {
    status.value = "submitted";
    sessionId.value = event.session_id;

    const userConversation = messages.value.findLast(
      (message) => message.id === "_user",
    );
    if (!userConversation) {
      return;
    }

    userConversation.id = String(event.user_conversation?.id);
    userConversation.content = event.user_conversation?.content || "";
    userConversation.created = event.user_conversation?.created;
  };

  const appendAssistantChunk = (
    event: Extract<StreamEvent, { type: "chunk" }>,
  ) => {
    status.value = "streaming";

    const assistantMessage = ensureAssistantMessage(event.content || "");
    if (
      assistantMessage.content === event.content ||
      event.content === undefined
    ) {
      return;
    }

    assistantMessage.content += event.content;
  };

  const finalizeAssistantConversation = (
    event: Extract<StreamEvent, { type: "end" }>,
  ) => {
    if (status.value !== "abort") {
      status.value = "ready";
    }

    const assistantConversation = getLastAssistantMessage();
    if (!assistantConversation || status.value === "abort") {
      return;
    }

    assistantConversation.id = String(event.system_conversation.id);
    assistantConversation.created = event.system_conversation.created;
    assistantConversation.files = event?.daily_report_images || [];
  };

  const handleStreamEvent = (event: StreamEvent, disconnect: () => void) => {
    if (event.type == "start") {
      updateUserConversationFromEvent(event);
      return;
    }

    if (event.type == "chunk") {
      appendAssistantChunk(event);
      return;
    }

    if (event.type == "stopped") {
      status.value = "abort";
      return;
    }

    if (event.type == "end") {
      finalizeAssistantConversation(event);
      disconnect();
      return;
    }

    if (event.type == "error") {
      status.value = "error";
      messages.value.splice(-1);
    }
  };

  const handleStreamMessage = (
    eventS: MessageEvent<string>,
    disconnect: () => void,
  ) => {
    const event = JSON.parse(eventS.data) as StreamEvent;
    handleStreamEvent(event, disconnect);
  };

  const createEventSourceDisconnectHandler = (eventSource: EventSource) => {
    return () => eventSource.close();
  };

  const bindMessageEventListener = (
    eventSource: EventSource,
    disconnect: () => void,
  ) => {
    const onMessage = (eventS: MessageEvent<string>) => {
      handleStreamMessage(eventS, disconnect);
    };

    eventSource.addEventListener("message", onMessage);
  };

  const makeConnectionSSE = async () => {
    try {
      if (
        filters.value === undefined ||
        (promptText.value?.prompt || "") === ""
      ) {
        throw new Error("กรุณาเลือกตัวกรอง");
      }

      resetSSE();

      const body: RequestBodySendPromptAuto<
        RequestQuerySummaryReport | RequestQuerySummaryReportWithSale
      > = {
        context: promptText.value?.prompt,
        filter_data: filters.value,
        need_image: promptNeedImage.value,
        ...(promptSelect.value === "favorite"
          ? {
              favorite_prompt_id: promptId.value,
            }
          : {}),
        ...(!["favorite", "ask-ai"].includes(promptSelect.value)
          ? {
              preset_prompt_id: promptId.value,
            }
          : {}),
      };

      await callAPIConversation(body);
    } catch (err: any) {
      throw new Error(getErrorMessage(err), { cause: err });
    } finally {
      // call abort again to handle case send_message crash before emit stop event
    }
  };

  const sendConversation = async (promptContext: string) => {
    try {
      if (sessionId.value === undefined || channelId.value === undefined) {
        throw new Error("channelId or sessionId not found");
      }

      if ((promptContext || "") === "") {
        throw new Error("prompt context required");
      }

      const body: RequestBodySendPromptAuto<undefined> = {
        context: promptContext,
        session_id: sessionId.value,
      };

      await callAPIConversation(body);
    } catch (err: any) {
      throw new Error(getErrorMessage(err), { cause: err });
    } finally {
      // call abort again to handle case send_message crash before emit stop event
    }
  };

  const callAPIConversation = async (
    body: RequestBodySendPromptAuto<
      RequestQuerySummaryReport | RequestQuerySummaryReportWithSale | undefined
    >,
  ) => {
    // re-init AbortController
    try {
      status.value = "open";

      messages.value.push(createUserMessage(body));

      sendSessionAbortController.value = new AbortController();
      const res = await useApi<ResponseSendPromptAuto>(
        `/api/conversations${apiAutoPromptVersion.value}/send_prompt_auto/`,
        {
          method: "post",
          body: body,
          signal: sendSessionAbortController.value.signal,
        },
      );

      sessionId.value = res.session_id;
      channelId.value = res.channel_id;

      status.value = "submitted";

      sendSSEAbortController.value = bindStreamMessageToUi(channelId.value);
    } catch (err: any) {
      throw new Error(getErrorMessage(err), { cause: err });
    } finally {
      // call abort again to handle case send_message crash before emit stop event
    }
  };

  /**
   * bindStreamMessageToUi is a function to bind connectStream generator
   * to conversations state.
   */
  const bindStreamMessageToUi = (channelId: string | number) => {
    const sse = useSSE(channelId, (eventSource) => {
      bindMessageEventListener(
        eventSource,
        createEventSourceDisconnectHandler(eventSource),
      );
    });

    return sse;
  };

  const abortSendConversation = async () => {
    if (channelId.value === undefined) {
      return;
    }
    try {
      if (status.value === "open") {
        sendSessionAbortController.value.abort();
      } else {
        await useApi<{ stop: boolean; channel_id: string }>(
          `/api/sse${apiAutoPromptVersion.value}/channel/${channelId.value}/stop/`,
          {
            method: "post",
          },
        );
      }
    } catch (err: any) {
      throw new Error(getErrorMessage(err), { cause: err });
    } finally {
      status.value = "abort";

      ensureAssistantMessage("");

      const assistantConversation = getLastAssistantMessage();
      if (assistantConversation) {
        assistantConversation.id = `abort_assistant_${dayjs().toISOString()}`;
      }
    }
  };

  const resetAll = async () => {
    await abortSendConversation();
    resetSSE();

    sessionId.value = undefined;
    channelId.value = undefined;

    promptSelect.value = "summary";
    promptId.value = undefined;
    promptText.value = {
      name: "",
      prompt: "",
    };
    promptNeedImage.value = false;

    filters.value = undefined;
    messages.value = [];
    status.value = "ready";
    promptFilterOptions.value = [];

    isPrinting.value = false;
  };

  return {
    sessionId,
    channelId,
    messages,
    status,
    filters,
    promptSelect,
    promptId,
    promptText,
    promptNeedImage,
    promptFilterOptions,

    isSending,
    isPrinting,

    getFavoritePrompts,
    createFavoritePrompt,
    updateFavoritePromptById,
    deleteFavoritePromptById,
    toggleFavoritePromptById,

    getPresetPrompts,

    resetSSE,
    makeConnectionSSE,
    sendConversation,
    abortSendConversation,

    resetAll,
  };
});
